import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Card,
  Stack,
  Button,
  Container,
  Grid,
  TextField,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AnyFieldApi } from '@tanstack/react-form';
import Iconify from 'src/components/iconify';

import { visibleLanguages } from 'src/config/quill-text-editor-settings';
import { LanguageTabs } from 'src/components/language-tabs';
import { TabPanel } from 'src/components/language-tabs/TabPanel';
import { useGetContestQuery } from 'src/api/contests/getContest';
import { ContestSettings } from './ContestSettings';
import { useAppSearchParams } from 'src/hooks/useAppSearchParams';
import { RoundsSection } from './RoundsSection';
import { ContestType } from 'src/types/domain.types';
import { useUpdateContestMutation } from 'src/api/contests/updateContest';
import ContestScoreDialog from 'src/components/contest/ContestScoreDialog';
import { GamezomeImageFormControl } from 'src/components/form/GamezomeImageFormControl';
import { GameTypeEnum } from 'src/enums/contest.enum';
import { useGameStore } from 'src/store/useGame.store';
import { useGameForm } from 'src/store/useGameForm.context';
import { ParticipantsSection } from './ParticipantsSection';
import { ExportUsersCsvButton } from './ExportUsersCsvButton';

const defaultContest = {
  game_type: GameTypeEnum.PUBLIC,
  name_en: '',
  name_fr: '',
  name_ru: '',
  name_de: '',
  name_es: '',
  name_lt: '',
  name_sp: '',
  name_ca: '',
  tenant: '',
  status: 0,
  prize_pool: 0,
  cumulative_prize_pool: 0,
  currency: '',
  tile_image: '',
  gamezone_image_en: '',
  gamezone_image_fr: '',
  gamezone_image_ru: '',
  gamezone_image_de: '',
  gamezone_image_es: '',
  gamezone_image_lt: '',
  gamezone_image_sp: '',
  gamezone_image_ca: '',
  weeks: [],
} as unknown as ContestType;

export default function ContestPage() {
  const { contestId } = useAppSearchParams();

  const navigate = useNavigate();
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const { setIsPrivateCompetition } = useGameStore();

  const [value, setValue] = useState(0);

  const { data: contest, isLoading: isGetContestLoading } =
    useGetContestQuery(contestId);
  const { mutate: updateContest, isPending: isUpdateContestLoading } =
    useUpdateContestMutation(contestId);

  const form: any = useGameForm({
    defaultValues: contest ?? defaultContest,
    onSubmit: ({ value }) => {
      updateContest(value as ContestType);
    },
  });

  useEffect(() => {
    if (contest) {
      setIsPrivateCompetition(contest.game_type === GameTypeEnum.PRIVATE);
    }
  }, [contest, setIsPrivateCompetition]);

  const handleChange = (e: any) => {
    const { id, value } = e.target;

    form.setFieldValue(id, value);
  };

  const handleSubmit = () => {
    form.handleSubmit();
  };

  const handleChangeTabs = (_event: any, newValue: number) => {
    setValue(newValue);
  };

  const [mainTab, setMainTab] = useState(0);

  const handleChangeMainTabs = (_event: any, newValue: number) => {
    setMainTab(newValue);
  };

  return (
    <>
      <Helmet>
        <title> Contest </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Contest
          </Typography>
          <Stack direction='row' spacing={2} alignItems='center'>
            <Button
              variant='outlined'
              onClick={() => navigate('/dashboard/contests')}
              startIcon={<Iconify icon='eva:arrow-back-outline' />}
            >
              Back
            </Button>
            <Button
              variant='outlined'
              startIcon={<Iconify icon='eva:play-circle-outline' />}
              onClick={() => setScoreDialogOpen(true)}
              disabled={!contest?.weeks.length}
            >
              Run Scoring
            </Button>
            <ExportUsersCsvButton />
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:save-outline' />}
              onClick={() => handleSubmit()}
              disabled={isUpdateContestLoading}
            >
              Update
            </Button>
          </Stack>
        </Stack>

        <form.AppForm>
          <form onSubmit={form.handleSubmit}>
            {isGetContestLoading ? (
              <Box
                sx={{ display: 'flex', justifyContent: 'center' }}
                style={{ padding: 20 }}
              >
                <CircularProgress />
              </Box>
            ) : !contest ? (
              <Box
                sx={{ display: 'flex', justifyContent: 'center' }}
                style={{ padding: 20 }}
              >
                <Typography variant='h4' gutterBottom>
                  Contest not found
                </Typography>
              </Box>
            ) : (
              <>
                <form.MainTabs
                  value={mainTab}
                  onChange={handleChangeMainTabs}
                />

                <TabPanel value={mainTab} index={0}>
                  <Card style={{ padding: 20, marginTop: 20 }}>
                    <LanguageTabs value={value} onChange={handleChangeTabs} />
                    {visibleLanguages.map((lang, index) => (
                      <TabPanel key={lang.code} value={value} index={index}>
                        <Grid container spacing={3}>
                          <Grid item xs={8}>
                            <TextField
                              id={`name_${lang.code}`}
                              label='Name'
                              fullWidth
                              value={
                                contest[
                                  `name_${lang.code}` as keyof ContestType
                                ]
                              }
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <form.Field
                              name={`gamezone_image_${lang.code}`}
                              children={({ state, name }: AnyFieldApi) => {
                                return (
                                  <GamezomeImageFormControl
                                    value={state.value}
                                    name={name}
                                    lang={lang.code}
                                  />
                                );
                              }}
                            />
                          </Grid>
                        </Grid>
                      </TabPanel>
                    ))}

                    <Grid container spacing={3} sx={{ mt: 2 }}>
                      <ContestSettings form={form} />
                    </Grid>
                  </Card>
                </TabPanel>

                {/* Rounds Tab */}
                <TabPanel value={mainTab} index={1}>
                  {contest && <RoundsSection contest={contest} />}
                </TabPanel>

                {/* Leaderboard Tab */}
                <TabPanel value={mainTab} index={2}>
                  <div> Leaderboard will be here </div>
                </TabPanel>

                {/* Participants Tab */}
                <TabPanel value={mainTab} index={3}>
                  <ParticipantsSection />
                </TabPanel>
              </>
            )}
          </form>
        </form.AppForm>

        {scoreDialogOpen && contest && (
          <ContestScoreDialog
            weeks={contest.weeks}
            contestId={contestId}
            onClose={() => setScoreDialogOpen(false)}
          />
        )}
      </Container>
    </>
  );
}
